import type { MatchHole } from './match-holes';

export type ScoreReport = {
  reporterParticipantId: string;
  subjectParticipantId: string;
  holeNumber: number;
  strokes: number;
};

export type ScorePick = {
  pickerParticipantId: string;
  subjectParticipantId: string;
  holeNumber: number;
  strokes: number;
};

export type HoleScoreDraft = {
  me: number | null;
  opponent: number | null;
  meDirty?: boolean;
  opponentDirty?: boolean;
};

export type HoleScoreDrafts = Record<number, HoleScoreDraft>;

type SavedHoleScore = {
  holeNumber: number;
  strokes: number;
};

export function findReport(
  reports: ScoreReport[],
  reporterParticipantId: string,
  subjectParticipantId: string,
  holeNumber: number,
) {
  return reports.find(report => (
    report.reporterParticipantId === reporterParticipantId
    && report.subjectParticipantId === subjectParticipantId
    && report.holeNumber === holeNumber
  ));
}

export function displayedStrokes(
  reports: ScoreReport[],
  viewerParticipantId: string,
  subjectParticipantId: string,
  holeNumber: number,
) {
  const mine = findReport(reports, viewerParticipantId, subjectParticipantId, holeNumber);
  if (mine) {
    return { strokes: mine.strokes, source: 'mine' as const };
  }
  const theirs = reports.find(report => (
    report.reporterParticipantId !== viewerParticipantId
    && report.subjectParticipantId === subjectParticipantId
    && report.holeNumber === holeNumber
  ));
  if (theirs) {
    return { strokes: theirs.strokes, source: 'theirs' as const };
  }
  return { strokes: null, source: null };
}

export function reportedValues(
  reports: ScoreReport[],
  subjectParticipantId: string,
  holeNumber: number,
) {
  return [...new Set(
    reports
      .filter(report => report.subjectParticipantId === subjectParticipantId && report.holeNumber === holeNumber)
      .map(report => report.strokes),
  )].sort((left, right) => left - right);
}

export function coverageComplete(
  participantIds: string[],
  holeNumbers: number[],
  reports: ScoreReport[],
) {
  if (!participantIds.length || !holeNumbers.length) {
    return false;
  }
  return holeNumbers.every(holeNumber => (
    participantIds.every(subjectParticipantId => reportedValues(reports, subjectParticipantId, holeNumber).length > 0)
  ));
}

export function reportsFromLegacyHoleStats(
  reports: ScoreReport[] | undefined,
  participants: { id: string; holeStats?: SavedHoleScore[] }[],
): ScoreReport[] {
  if (reports && reports.length > 0) {
    return reports;
  }
  return participants.flatMap(participant =>
    (participant.holeStats ?? []).map(stat => ({
      reporterParticipantId: participant.id,
      subjectParticipantId: participant.id,
      holeNumber: stat.holeNumber,
      strokes: stat.strokes,
    })),
  );
}

export function buildHoleScoreDrafts(
  holes: MatchHole[],
  viewerParticipantId: string,
  opponentParticipantId: string | undefined,
  reports: ScoreReport[] = [],
): HoleScoreDrafts {
  return holes.reduce<HoleScoreDrafts>((drafts, hole) => {
    drafts[hole.number] = {
      me: displayedStrokes(reports, viewerParticipantId, viewerParticipantId, hole.number).strokes,
      opponent: opponentParticipantId
        ? findReport(reports, viewerParticipantId, opponentParticipantId, hole.number)?.strokes ?? null
        : null,
    };
    return drafts;
  }, {});
}

export function mergeLiveScoreDrafts(
  drafts: HoleScoreDrafts,
  holes: MatchHole[],
  viewerParticipantId: string,
  opponentParticipantId: string | undefined,
  reports: ScoreReport[] = [],
): HoleScoreDrafts {
  const next: HoleScoreDrafts = { ...drafts };
  for (const hole of holes) {
    const current = next[hole.number] ?? { me: null, opponent: null };
    next[hole.number] = {
      me: current.meDirty
        ? current.me
        : displayedStrokes(reports, viewerParticipantId, viewerParticipantId, hole.number).strokes,
      opponent: current.opponentDirty
        ? current.opponent
        : opponentParticipantId
          ? findReport(reports, viewerParticipantId, opponentParticipantId, hole.number)?.strokes ?? null
          : null,
      meDirty: current.meDirty,
      opponentDirty: current.opponentDirty,
    };
  }
  return next;
}

export function firstUnscoredHoleIndex(
  holes: MatchHole[],
  reports: ScoreReport[],
  viewerParticipantId: string,
) {
  const index = holes.findIndex(hole => !findReport(reports, viewerParticipantId, viewerParticipantId, hole.number));
  return index === -1 ? Math.max(holes.length - 1, 0) : index;
}

export function savedHoleScoreLabel(
  myScore?: number | null,
  opponentScore?: number | null,
) {
  if (myScore == null && opponentScore == null) {
    return '';
  }
  return `${myScore ?? '–'}:${opponentScore ?? '–'}`;
}

export function hasReporterEntries(reports: ScoreReport[], reporterParticipantId: string) {
  return reports.some(report => report.reporterParticipantId === reporterParticipantId);
}

export function stepScore(value: number | null, delta: number) {
  if (value == null) {
    return delta > 0 ? 1 : null;
  }
  const next = value + delta;
  if (next < 1) {
    return null;
  }
  return Math.min(20, next);
}
